import React, { useEffect, useState } from 'react';

// MUI
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  InputLabel,
  Box,
  IconButton,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import OlcButton from '~/components/General/OlcButton';

// Assets
import { DetailsCrossCIcon, DetailsErrorCIcon, ModalClosePlansCIcon } from '~/assets/images';
import CloseIcon from '@mui/icons-material/Close';

// Styles
import './styles.scss';

const DelayDialogNew = ({
  open,
  onClose,
  onExited,
  editingStep,
  delayConfig,
  setDelayConfig,
  handleSaveDelay,
  delayLoading,
}) => {
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const value = e.target.value;
    const num = parseInt(value, 10);
    if (!value || isNaN(num)) {
      setDelayConfig({ days: '' });
      setError('Delay period must be less than 365 days.');
    } else if (num < 1 || num > 365) {
      setDelayConfig({ days: num });
      setError('Delay period must be less than 365 days.');
    } else {
      setDelayConfig({ days: num });
      setError('');
    }
  };

  const canSave = delayConfig.days >= 1 && delayConfig.days <= 365 && !error;

  useEffect(() => {
    if (open) {
      // Reset error when modal opens
      setError('');
      // Always set default value to 1 when adding a new delay (not editing)
      if (!editingStep) {
        setDelayConfig({ days: 1 });
      }
    }
    // Note: We don't reset here to avoid glitch during closing animation
    // The reset is handled in handleExited after modal fully closes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingStep]);

  const handleClose = () => {
    if (!delayLoading) {
      onClose();
    }
  };

  const handleExited = () => {
    // Reset input field to default value 1 after modal has fully closed
    setDelayConfig({ days: 1 });
    // Call the original onExited callback if provided
    if (onExited) {
      onExited();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      onExited={handleExited}
      maxWidth="sm"
      fullWidth
      className="delayDialogNew"
    >
      <DialogTitle className="delayDialogTitle">
        <Typography variant="h6" fontWeight="700" sx={{ fontFamily: 'Inter', color: '#ed5c2f' }}>
          {editingStep ? 'Edit Delay' : 'Add Delay'}
        </Typography>
        <IconButton onClick={handleClose} className="closeIcon" disabled={delayLoading}>
          <img src={DetailsCrossCIcon} alt="close" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="delayDialogContent">
        <Box className="formFieldWrapper">
          <InputLabel htmlFor="Campaign Name" className="inputLabel">
            {editingStep
              ? 'Update the waiting period'
              : 'Add a waiting period in days before the next mail action'}
          </InputLabel>
          <TextField
            fullWidth
            autoComplete="off"
            name="days"
            type="number"
            className="inputField"
            value={delayConfig.days}
            onChange={handleChange}
            inputProps={{ min: 1, max: 365 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                  {delayConfig.days && (
                    <IconButton
                      className="cpClearInputBtn"
                      size="small"
                      onClick={() => {
                        setDelayConfig({ days: '' });
                        setError('');
                      }}
                      edge="end"
                      sx={{ padding: '4px' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
            error={!!error}
          />
          {error && (
            <Typography
              variant="body2"
              className="helperText"
              color={error ? 'error' : 'textSecondary'}
              sx={{ mt: 1 }}
              style={{ color: error ? '#f44336' : '#6b6b6b' }}
            >
              <img src={DetailsErrorCIcon} alt="error" style={{ marginRight: '8px' }} />{' '}
              <span>{error}</span>
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions className="delayDialogActions">
        <OlcButton
          variant="secondary"
          onClick={handleClose}
          disabled={delayLoading}
          sx={{
            textTransform: 'capitalize',
            color: '#303030',
            fontFamily: 'Inter',
          }}
        >
          Cancel
        </OlcButton>
        <OlcButton
          variant="primary"
          onClick={handleSaveDelay}
          disabled={!canSave || delayLoading}
          className="delay-btn"
          sx={{
            borderRadius: 1,
            textTransform: 'capitalize',
            backgroundColor: '#ed5c2f',
            fontFamily: 'Inter',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#ed5c2f',
            },
          }}
        >
          {delayLoading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
              {editingStep ? 'Updating...' : 'Adding...'}
            </>
          ) : (
            'Save'
          )}
        </OlcButton>
      </DialogActions>
    </Dialog>
  );
};

export default DelayDialogNew;
